/**
 * SettingsLegalFooter — Legal links section for Settings
 *
 * Appears at the bottom of SettingsList (mobile variant) and
 * pinned at the bottom of SettingsModal (desktop variant).
 *
 * WP-1 + RWP-A of FS-016.
 */
import { Link } from "react-router-dom";
import { cn } from "../utils/cn";

interface SettingsLegalFooterProps {
  /** "mobile" renders full vertical layout; "desktop" renders compact horizontal */
  variant: "mobile" | "desktop";
}

/** SVG icon: document/terms */
function DocumentIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn("icon", className)}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

/** SVG icon: shield/privacy */
function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn("icon", className)}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

/** SVG icon: accessibility */
function AccessibilityIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn("icon", className)}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
      />
    </svg>
  );
}

/** SVG icon: mail */
function MailIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn("icon", className)}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

/** Shield icon for data-handling statement (green) */
function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={cn("shield-icon", className)}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

export function SettingsLegalFooter({ variant }: SettingsLegalFooterProps) {
  if (variant === "desktop") {
    return (
      <div
        className="settings-modal-legal-footer"
        id="settings-legal-footer-desktop"
      >
        <Link to="/terms" className="settings-modal-legal-link">
          Terms of Service
        </Link>
        <Link to="/privacy" className="settings-modal-legal-link">
          Privacy Policy
        </Link>
        <Link to="/accessibility" className="settings-modal-legal-link">
          Accessibility Statement
        </Link>
        <a
          href="mailto:legal@retireez.ca"
          className="settings-modal-legal-link"
        >
          legal@retireez.ca
        </a>
      </div>
    );
  }

  // Mobile variant — full vertical layout
  return (
    <div className="settings-legal-footer" id="settings-legal-footer-mobile">
      <h2 className="settings-legal-header">Legal & Privacy</h2>

      <nav aria-label="Legal links">
        <Link to="/terms" className="settings-legal-link">
          <DocumentIcon />
          <span>Terms of Service</span>
        </Link>

        <Link to="/privacy" className="settings-legal-link">
          <ShieldIcon />
          <span>Privacy Policy</span>
        </Link>

        <Link to="/accessibility" className="settings-legal-link">
          <AccessibilityIcon />
          <span>Accessibility Statement</span>
        </Link>

        <a href="mailto:legal@retireez.ca" className="settings-legal-link">
          <MailIcon />
          <span>legal@retireez.ca</span>
        </a>
      </nav>

      <div className="settings-legal-statement">
        <ShieldCheckIcon />
        Your financial data is encrypted and stored securely. We never sell or
        share your data.
      </div>
    </div>
  );
}
